import React, { useCallback, useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { Bounce, toast } from "react-toastify"
import PageHeader from "../../../components/DashboardComponents/PageHeader"
import CustomButton from "../../../components/OnboardingComponents/CustomButton"
import CustomInput from "../../../components/OnboardingComponents/CustomInput"
import EmptyState from "../../../components/Common/EmptyState"
import { ICONS } from "../../../constants/icons"
import { get, post } from "../../../api/axios"
import endpoints from "../../../api/endpoints"
import { toastApiError } from "../../../helpers/apiError"
import { reportFormApiError } from "../../../helpers/formFieldErrors"
import {
  applyPermissionToggle,
  buildDefaultPermissionsFromModules,
  mapPermissionGroupsToModules,
  mapPermissionSelectionsToIds,
  mapSelectedIdsToPermissionSelections,
} from "./adminRolePermissionsData"
import RolePermissionsMatrix from "./RolePermissionsMatrix"

/**
 * Admin Create / Edit Role — permissions from GET /api/v1/admin/permissions/get-all
 */
const AdminCreateRole = () => {
  const navigate = useNavigate()
  const { roleId } = useParams()
  const isEdit = Boolean(roleId)

  const [name, setName] = useState("")
  const [description, setDescription] = useState("")
  const [modules, setModules] = useState([])
  const [permissions, setPermissions] = useState({})
  const [saving, setSaving] = useState(false)
  const [fieldErrors, setFieldErrors] = useState({})
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState(null)

  const bootstrap = useCallback(async () => {
    setIsLoading(true)
    setLoadError(null)
    try {
      const permissionsResponse = await get(endpoints.getAdminPermissions)
      if (!permissionsResponse?.data?.success) {
        setLoadError(
          toastApiError(permissionsResponse, "Failed to load permissions"),
        )
        return
      }

      const nextModules = mapPermissionGroupsToModules(
        permissionsResponse.data.result || [],
      )
      setModules(nextModules)

      let nextSelections = buildDefaultPermissionsFromModules(nextModules)

      if (roleId) {
        const roleResponse = await get(endpoints.getAdminRoleById(roleId), {
          params: { "with[]": ["permissions", "users"] },
        })
        if (!roleResponse?.data?.success) {
          setLoadError(toastApiError(roleResponse, "Failed to load role"))
          return
        }

        const result = roleResponse.data.result || {}
        setName(result.name || "")
        setDescription(result.description || "")
        const selectedIds = (result.permissions || [])
          .map(item => item.id)
          .filter(Boolean)
        nextSelections = mapSelectedIdsToPermissionSelections(
          nextModules,
          selectedIds,
        )
      }

      setPermissions(nextSelections)
    } catch (err) {
      setLoadError(
        toastApiError(
          err,
          isEdit ? "Failed to load role" : "Failed to load permissions",
        ),
      )
    } finally {
      setIsLoading(false)
    }
  }, [isEdit, roleId])

  useEffect(() => {
    document.title = isEdit
      ? "Edit Role | AMOK . EARTH Admin"
      : "Create Role | AMOK . EARTH Admin"
  }, [isEdit])

  useEffect(() => {
    bootstrap()
  }, [bootstrap])

  const handlePermissionChange = (targetId, level) => {
    setFieldErrors(prev =>
      prev.permissions ? { ...prev, permissions: "" } : prev,
    )
    setPermissions(prev => {
      const module = modules.find(item => item.id === targetId)
      return {
        ...prev,
        [targetId]: applyPermissionToggle(
          prev[targetId],
          level,
          module?.availableLevels,
        ),
      }
    })
  }

  const handleCancel = () => {
    if (isEdit && roleId) {
      navigate(`/admin/roles/${roleId}`)
      return
    }
    navigate("/admin/roles")
  }

  const handleSave = async event => {
    event.preventDefault()

    if (!name.trim()) {
      setFieldErrors({ roleName: "Role name is required." })
      return
    }
    setFieldErrors({})

    setSaving(true)
    try {
      const payload = {
        name: name.trim(),
        permissions: mapPermissionSelectionsToIds(modules, permissions),
      }
      if (description.trim()) {
        payload.description = description.trim()
      }

      const response = isEdit
        ? await post(endpoints.updateAdminRole(roleId), payload, {
            skipErrorToast: true,
          })
        : await post(endpoints.createAdminRole, payload, {
            skipErrorToast: true,
          })

      if (
        !response ||
        response.status >= 400 ||
        response?.data?.success === false
      ) {
        reportFormApiError(
          response,
          isEdit ? "Failed to update role" : "Failed to create role",
          { setFieldErrors },
          { name: "roleName", permissions: "permissions" },
        )
        return
      }

      toast.success(
        response?.data?.message ||
          (isEdit
            ? `Role “${name.trim()}” updated.`
            : `Role “${name.trim()}” saved.`),
        {
          position: "bottom-center",
          theme: "colored",
          transition: Bounce,
        },
      )

      const nextId = response?.data?.result?.id || roleId
      if (nextId) {
        navigate(`/admin/roles/${nextId}`)
      } else {
        navigate("/admin/roles")
      }
    } catch (err) {
      reportFormApiError(
        err,
        isEdit ? "Failed to update role" : "Failed to create role",
        { setFieldErrors },
        { name: "roleName", permissions: "permissions" },
      )
    } finally {
      setSaving(false)
    }
  }

  if (isLoading) {
    return (
      <div
        className="admin-page admin-roles-create"
        aria-busy="true"
        aria-label="Loading role form"
      >
        <div className="admin-roles-create__header admin-roles-create__header--skeleton">
          <div className="admin-roles-create__skeleton admin-roles-create__skeleton--title" />
          <div className="admin-roles-create__skeleton admin-roles-create__skeleton--subtitle" />
        </div>
        <div className="admin-roles-create__form">
          <div className="admin-roles-create__fields">
            <div className="admin-roles-create__skeleton admin-roles-create__skeleton--field" />
            <div className="admin-roles-create__skeleton admin-roles-create__skeleton--textarea" />
          </div>
          <div className="admin-roles-create__permissions admin-roles-create__permissions--skeleton">
            {Array.from({ length: 8 }).map((_, index) => (
              <div
                key={`perm-skel-${index}`}
                className="admin-roles-create__skeleton admin-roles-create__skeleton--row"
              />
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (loadError && modules.length === 0) {
    return (
      <div className="admin-page admin-roles-create">
        <EmptyState
          title="Unable to load"
          description={loadError}
          actionText="Try again"
          onActionClick={bootstrap}
        />
        <CustomButton
          variant="secondary"
          size="md"
          type="button"
          onClick={() => navigate("/admin/roles")}
        >
          Back to Roles
        </CustomButton>
      </div>
    )
  }

  return (
    <div className="admin-page admin-roles-create">
      <PageHeader
        className="admin-roles-create__header"
        title={isEdit ? "Edit Role" : "Create Role"}
        subtitle={
          isEdit
            ? "Update role detail and permissions."
            : "Define role detail and set permissions to control access across platform."
        }
        actions={
          <>
            <CustomButton
              variant="secondary"
              size="md"
              type="button"
              onClick={handleCancel}
              disabled={saving}
            >
              Cancel
            </CustomButton>
            <CustomButton
              variant="primary"
              size="md"
              type="submit"
              form="admin-create-role-form"
              loading={saving}
              disabled={saving}
              trailingIconSrc={ICONS.arrowRight12}
              trailingIconAlt=""
            >
              {isEdit ? "Update Role" : "Save Role"}
            </CustomButton>
          </>
        }
      />

      <form
        id="admin-create-role-form"
        className="admin-roles-create__form"
        onSubmit={handleSave}
      >
        <div className="admin-roles-create__fields">
          <CustomInput
            label="Role Name"
            name="roleName"
            placeholder="Enter role name"
            value={name}
            onChange={event => {
              setName(event.target.value)
              if (fieldErrors.roleName) {
                setFieldErrors(prev => ({ ...prev, roleName: "" }))
              }
            }}
            error={fieldErrors.roleName}
          />
          <CustomInput
            label="Description"
            name="description"
            textarea
            placeholder="Describe the purpose and scope of this role..."
            value={description}
            onChange={event => setDescription(event.target.value)}
          />
        </div>

        <RolePermissionsMatrix
          modules={modules}
          permissions={permissions}
          onChange={handlePermissionChange}
          error={fieldErrors.permissions}
        />
      </form>
    </div>
  )
}

export default AdminCreateRole
